import { useFormik } from "formik";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { useCopyCustomReportMutation } from "../../api/network/reportApiServices";
import { setModalsData, TModalsState } from "../../api/store/commonSlice";
import {
  AdminFormFieldInput,
  AdminFormFieldSubmit,
} from "../../components/admin/formFields";
import FTModal from "../../components/common/FTModal";
import { APP_CONFIG } from "../../constants/constants";
import { useReportUtils } from "./hooks/reportUtils";

type ModalShareViaEmailProps = {
  reportId?: string;
  handleEdit?: () => void;
};

const ModalMakeCopy: FC<ModalShareViaEmailProps> = ({
  reportId,
  handleEdit,
}) => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", {
    keyPrefix: "reports.copyReport",
  });
  const { t: tReport } = useTranslation("translation", {
    keyPrefix: "reports",
  });
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);

  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );

  const { navigateOnReportOptionsChange, refetchCustomReportListToStore } =
    useReportUtils();

  // share report via email mutation
  const [copyReport] = useCopyCustomReportMutation();

  const hideModal = () => {
    dispatch(setModalsData({ ...modalsState, showModalMakeCopy: false }));
  };

  const formik = useFormik({
    initialValues: { report_name: "" },
    validationSchema: Yup.object({
      report_name: Yup.string().required("Report name is required"),
    }),
    onSubmit: (values) => {
      copyReport({ id: Number(reportId), name: values.report_name })
        .unwrap()
        .then((response) => {
          // re-fetch the reports and update the store
          refetchCustomReportListToStore();

          hideModal();
          toast.success(t("copy_created"), { autoClose: 3000 });
          navigateOnReportOptionsChange({
            filterValues: response.report_filters?.reduce((acc, filter) => {
              const field_config_key = Object.keys(
                APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS
              ).find(
                (config_key) =>
                  APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS[
                    config_key as keyof typeof APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS
                  ]?.field_name === filter.param
              );
              const field_config =
                APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS[
                  field_config_key as keyof typeof APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS
                ];
              if (!!field_config && !!field_config?.multiple) {
                acc[filter.param] = filter.value
                  .split(",")
                  .map((item: any) => ({ label: item, value: item }));
              } else {
                acc[filter.param] = filter.value;
              }
              return acc;
            }, {} as any),
            reportType: response?.type?.toString(),
            category: response.category,
            reportId: response.id,
            navigate,
            reportFilters: response.report_filters,
          });
        })
        .catch((error) => {
          console.log(error);
        });
    },
  });

  return (
    <FTModal
      visible={modalsState.showModalMakeCopy}
      dismissible={true}
      heading={!step ? t("modify_copy_report") : t("rename_report")}
      onHide={hideModal}
      containerStyle="max-w-[calc(100vw-4rem)] w-[450px]"
      position="center"
      positionHorizontal="center"
    >
      {!reportId ? (
        <>No report id</>
      ) : (
        <>
          {" "}
          {!step ? (
            <>
              <div className="mt-4 text-lg text-gray-400">
                {t("report_use")}
              </div>
              <AdminFormFieldSubmit
                customWrapperClass="mt-10"
                label={tReport("make_copy_button")}
                variant="primary"
                type="button"
                onClick={() => {
                  setStep(1);
                }}
              />
              <AdminFormFieldSubmit
                customWrapperClass="mt-6"
                label={t("continue_edit")}
                variant="primary-alt"
                type="button"
                onClick={() => {
                  if (handleEdit) handleEdit();
                  // hideModal();
                }}
              />
            </>
          ) : (
            <>
              <form onSubmit={formik.handleSubmit} className="mt-6">
                <AdminFormFieldInput
                  label={t("report_name")}
                  type="text"
                  id="report_name"
                  name="report_name"
                  value={formik.values.report_name}
                  onChange={formik.handleChange}
                />
                <AdminFormFieldSubmit
                  customWrapperClass="mt-6"
                  label={tMain("confirm")}
                  variant="primary"
                  type="submit"
                  disabled={!formik.isValid}
                />

                <AdminFormFieldSubmit
                  customWrapperClass="mt-6"
                  variant="danger-transparent"
                  type="button"
                  label={tMain("cancel")}
                  onClick={hideModal}
                />
              </form>
            </>
          )}
        </>
      )}
    </FTModal>
  );
};

export default ModalMakeCopy;
